import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";

import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";
import AssignmentLateOutlinedIcon from "@mui/icons-material/AssignmentLateOutlined";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import AddIcon from "@mui/icons-material/Add";

const DashboardView = () => {
  const [timePeriod, setTimePeriod] = useState("Daily");
  const [selectedVisit, setSelectedVisit] = useState(0);
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const detectMode = () => {
      const doc = document.documentElement;
      const body = document.body;

      const allElements = Array.from(document.querySelectorAll("button, a, div, span"));
      const dayBtnExists = allElements.some((el) =>
        el.textContent && el.textContent.trim().toLowerCase().includes("activate day mode")
      );

      const hasDarkClass =
        doc.classList.contains("dark") ||
        doc.classList.contains("dark-theme") ||
        body.classList.contains("dark-theme") ||
        doc.getAttribute("data-theme") === "dark" ||
        body.getAttribute("data-theme") === "dark";

      setIsDark(dayBtnExists || hasDarkClass);
    };

    detectMode();
    const interval = setInterval(detectMode, 250);
    return () => clearInterval(interval);
  }, []);

  const cardBg = isDark ? "#0F1F33" : "#FFFFFF";
  const cardBorder = isDark ? "rgba(255, 255, 255, 0.08)" : "#E2E8F0";
  const primaryTextColor = isDark ? "#F8FAFC" : "#0B2540";
  const secondaryTextColor = isDark ? "#94A3B8" : "#64748B";
  const tealMain = "#1E5E5F";
  const iconCircleBg = isDark ? "rgba(30, 94, 95, 0.35)" : "#D9EBEF";
  const iconColor = isDark ? "#38BDF8" : tealMain;
  const gridLineColor = isDark ? "rgba(255, 255, 255, 0.05)" : "#E2E8F0";

  const kpiCards = [
    {
      title: "Payments",
      value: "$1,240.00",
      subText: "Invoiced Amount",
      icon: <MonetizationOnOutlinedIcon sx={{ color: iconColor, fontSize: "1.45rem" }} />,
    },
    {
      title: "Received Amount",
      value: "$980.00",
      subText: "Paid Amount",
      icon: <TrendingUpRoundedIcon sx={{ color: iconColor, fontSize: "1.45rem" }} />,
    },
    {
      title: "Remaining Amount",
      value: "$260.00",
      subText: "Outstanding",
      icon: <AssignmentLateOutlinedIcon sx={{ color: iconColor, fontSize: "1.45rem" }} />,
    },
    {
      title: "Posted Amount",
      value: "$840.00",
      subText: "Posted",
      icon: <VerifiedUserOutlinedIcon sx={{ color: iconColor, fontSize: "1.45rem" }} />,
    },
    {
      title: "Patients",
      value: "24",
      subText: "Total Active",
      icon: <PeopleAltOutlinedIcon sx={{ color: iconColor, fontSize: "1.45rem" }} />,
    },
    {
      title: "Visits",
      value: "12",
      subText: "Total Appointments",
      icon: <LocalHospitalOutlinedIcon sx={{ color: iconColor, fontSize: "1.45rem" }} />,
    },
    {
      title: "Upcoming Appointments",
      value: "4",
      subText: "Scheduled",
      icon: <ScheduleOutlinedIcon sx={{ color: iconColor, fontSize: "1.45rem" }} />,
    },
    {
      title: "Unsigned Notes",
      value: "18",
      subText: "Action Required",
      icon: <AssignmentLateOutlinedIcon sx={{ color: iconColor, fontSize: "1.45rem" }} />,
    },
  ];

  const graphPoints = [
    { x: 50, y: 190, date: "20 Jul" },
    { x: 190, y: 40, date: "21 Jul" },
    { x: 330, y: 265, date: "24 Jul" },
    { x: 470, y: 190, date: "28 Jul" },
    { x: 610, y: 265, date: "31 Jul" },
    { x: 750, y: 190, date: "4 Aug" },
    { x: 890, y: 265, date: "7 Aug" },
  ];

  const visits = [
    { name: "Asif Ali", date: "Aug 6, 2026", type: "Neurology Consultation", amount: "$150.00", status: "Paid", claimId: "CLM-9041" },
    { name: "kamal jamal", date: "Aug 7, 2026", type: "EEG Brain Scan", amount: "$320.00", status: "Pending", claimId: "CLM-9042" },
    { name: "Test Ahmad", date: "Jul 20, 2026", type: "Follow-up Exam", amount: "$95.00", status: "Paid", claimId: "CLM-9043" },
    { name: "kamal jamal", date: "Jul 16, 2026", type: "MRI Diagnostics", amount: "$650.00", status: "Verified", claimId: "CLM-9044" },
  ];

  const activeClaim = visits[selectedVisit];

  const recentEncounters = [
    { id: "ENC-8041", patient: "Sarah Jenkins", age: "34", condition: "Migraine Evaluation", time: "10:30 AM", status: "Completed", doctor: "Dr. Usman" },
    { id: "ENC-8042", patient: "Robert Fox", age: "52", condition: "Epilepsy Follow-up", time: "11:15 AM", status: "In Progress", doctor: "Dr. Usman" },
    { id: "ENC-8043", patient: "Elena Rostova", age: "29", condition: "Neuropathy Screening", time: "01:00 PM", status: "Scheduled", doctor: "Dr. Smith" },
    { id: "ENC-8044", patient: "Marcus Brody", age: "45", condition: "Post-Concussion Check", time: "02:30 PM", status: "Scheduled", doctor: "Dr. Usman" },
  ];

  return (
    <Box sx={{ width: "100%", pb: 2, overflow: "visible" }}>
      {/* 1. Header Banner */}
      <Box
        sx={{
          width: "100%",
          boxSizing: "border-box",
          px: { xs: 2.5, md: 3.5 },
          py: { xs: 2.5, md: 3 },
          mb: 3,
          borderRadius: "14px",
          background: isDark
            ? "linear-gradient(90deg, #071524 0%, #0B2540 60%, #11355B 100%)"
            : "linear-gradient(90deg, #112D47 0%, #153856 50%, #1E5E5F 100%)",
          color: "#FFFFFF",
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          alignItems: { xs: "flex-start", lg: "center" },
          justifyContent: "space-between",
          gap: 2.5,
          boxShadow: isDark ? "0 6px 20px rgba(0,0,0,0.4)" : "0 4px 18px rgba(11, 37, 64, 0.12)",
        }}
      >
        <Box>
          <Typography sx={{ fontSize: "0.85rem", fontWeight: 500, color: "rgba(255, 255, 255, 0.75)", mb: 0.3 }}>
            Dashboard
          </Typography>
          <Typography sx={{ fontSize: { xs: "1.65rem", md: "1.95rem" }, fontWeight: 800, mb: 0.6, color: "#FFFFFF" }}>
            Welcome, NeuroEZ!
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: "0.8rem", color: "rgba(255, 255, 255, 0.85)" }}>
            <span>September 15, 2026</span>
            <span style={{ opacity: 0.5 }}>|</span>
            <span>Your practice at a glance</span>
          </Box>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 1.5, flexWrap: "nowrap" }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1.2,
              px: 2,
              py: 0.9,
              borderRadius: "10px",
              backgroundColor: "rgba(255, 255, 255, 0.12)",
              backdropFilter: "blur(8px)",
              border: "1px solid rgba(255, 255, 255, 0.22)",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            <Box sx={{ width: 22, height: 22, borderRadius: "5px", backgroundColor: "#FFFFFF", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ color: "#112D47", fontSize: "0.82rem", fontWeight: 900 }}>✓</span>
            </Box>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: "#FFFFFF !important" }}>
              Sign Notes
            </Typography>
            <Box sx={{ minWidth: 20, height: 20, px: 0.5, borderRadius: "50%", backgroundColor: "#EF4444", color: "#FFFFFF", fontSize: "0.7rem", fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center" }}>
              18
            </Box>
          </Box>

          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              px: 2,
              py: 0.9,
              borderRadius: "10px",
              backgroundColor: "#FFFFFF",
              color: "#0B2540",
              cursor: "pointer",
              boxShadow: "0 2px 10px rgba(0, 0, 0, 0.12)",
              whiteSpace: "nowrap",
            }}
          >
            <Box sx={{ width: 22, height: 22, borderRadius: "50%", backgroundColor: "#D9EBEF", display: "flex", alignItems: "center", justifyContent: "center", color: "#1E5E5F" }}>
              <AddIcon sx={{ fontSize: "0.95rem !important" }} />
            </Box>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: "#0B2540" }}>
              New Appointment
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* 2. Eight KPI Cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            lg: "repeat(4, 1fr)",
          },
          gap: 2.2,
          mb: 4,
        }}
      >
        {kpiCards.map((card, index) => (
          <Box
            key={index}
            sx={{
              p: "18px 20px",
              borderRadius: "14px",
              backgroundColor: cardBg,
              border: `1.5px solid ${cardBorder}`,
              boxShadow: isDark ? "0 4px 18px rgba(0, 0, 0, 0.35)" : "0 2px 8px rgba(11, 37, 64, 0.04)",
              display: "flex",
              alignItems: "center",
              gap: 2,
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                transform: "translateY(-3px)",
                borderColor: tealMain,
              },
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                minWidth: 48,
                borderRadius: "50%",
                backgroundColor: iconCircleBg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {card.icon}
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: "1.32rem", fontWeight: 800, color: primaryTextColor, lineHeight: 1.15 }}>
                {card.value}
              </Typography>
              <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: primaryTextColor, mt: 0.3, lineHeight: 1.2 }}>
                {card.title}
              </Typography>
              {card.subText && (
                <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: secondaryTextColor, mt: 0.2 }}>
                  {card.subText}
                </Typography>
              )}
            </Box>
          </Box>
        ))}
      </Box>

      {/* 3. Appointment Trends Graph */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "14px",
          border: `1.5px solid ${cardBorder}`,
          p: { xs: 2.5, md: 3 },
          mb: 4,
          overflow: "hidden",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5, flexWrap: "wrap", gap: 1.5 }}>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
              <Typography sx={{ fontSize: "1.05rem", fontWeight: 800, color: primaryTextColor, letterSpacing: "-0.2px" }}>
                Appointment Trends
              </Typography>
              <Chip
                label="Live Feed"
                size="small"
                sx={{
                  height: "20px",
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  backgroundColor: iconCircleBg,
                  color: isDark ? "#38BDF8" : tealMain,
                  borderRadius: "5px",
                }}
              />
            </Box>
            <Typography sx={{ fontSize: "0.78rem", color: secondaryTextColor, fontWeight: 500, mt: 0.2 }}>
              Real-time appointment volume and scheduling velocity
            </Typography>
          </Box>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="time-period-label" sx={{ fontSize: "0.8rem", color: secondaryTextColor }}>Period</InputLabel>
            <Select
              labelId="time-period-label"
              value={timePeriod}
              label="Period"
              onChange={(e) => setTimePeriod(e.target.value)}
              sx={{
                borderRadius: "8px",
                fontSize: "0.82rem",
                fontWeight: 600,
                color: primaryTextColor,
                height: "36px",
                backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "#FFFFFF",
                "& fieldset": { borderColor: cardBorder },
                "&:hover fieldset": { borderColor: tealMain },
                "&.Mui-focused fieldset": { borderColor: tealMain },
              }}
            >
              <MenuItem value="Daily">Daily</MenuItem>
              <MenuItem value="Weekly">Weekly</MenuItem>
              <MenuItem value="Monthly">Monthly</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ width: "100%", height: "auto", overflow: "hidden" }}>
          <svg
            viewBox="0 0 940 340"
            preserveAspectRatio="xMidYMid meet"
            style={{ width: "100%", height: "auto", maxHeight: "320px", display: "block" }}
          >
            <defs>
              <linearGradient id="themeChartGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={isDark ? "#38BDF8" : tealMain} stopOpacity={isDark ? "0.35" : "0.22"} />
                <stop offset="100%" stopColor={isDark ? "#38BDF8" : tealMain} stopOpacity="0.0" />
              </linearGradient>
            </defs>

            {[{ val: "4", y: 40 }, { val: "3", y: 115 }, { val: "2", y: 190 }, { val: "1", y: 265 }].map((item, i) => (
              <g key={i}>
                <text x="38" y={item.y + 4} textAnchor="end" fill={secondaryTextColor} fontSize="11" fontWeight="600">{item.val}</text>
                <line x1="50" y1={item.y} x2="900" y2={item.y} stroke={gridLineColor} strokeDasharray="5 5" strokeWidth="1" />
              </g>
            ))}

            <text x="38" y="334" textAnchor="end" fill={secondaryTextColor} fontSize="11" fontWeight="600">0</text>
            <line x1="45" y1="330" x2="905" y2="330" stroke={cardBorder} strokeWidth="1.2" />

            <path
              d="M 50 190 C 100 90, 140 40, 190 40 C 240 40, 280 265, 330 265 C 380 265, 420 190, 470 190 C 520 190, 560 265, 610 265 C 660 265, 700 190, 750 190 C 800 190, 840 265, 890 265 L 890 330 L 50 330 Z"
              fill="url(#themeChartGradient)"
            />
            <path
              d="M 50 190 C 100 90, 140 40, 190 40 C 240 40, 280 265, 330 265 C 380 265, 420 190, 470 190 C 520 190, 560 265, 610 265 C 660 265, 700 190, 750 190 C 800 190, 840 265, 890 265"
              fill="none"
              stroke={isDark ? "#38BDF8" : tealMain}
              strokeWidth="3.2"
              strokeLinecap="round"
            />

            {graphPoints.map((pt, i) => (
              <g key={i}>
                <text x={pt.x} y="348" textAnchor="middle" fill={secondaryTextColor} fontSize="11" fontWeight="600">{pt.date}</text>
                <circle cx={pt.x} cy={pt.y} r="6" fill={isDark ? "#38BDF8" : tealMain} />
                <circle cx={pt.x} cy={pt.y} r="3" fill={cardBg} />
              </g>
            ))}
          </svg>
        </Box>
      </Box>

      {/* 4. Visits & Claim Details */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "340px 1fr" }, gap: 2.5, mb: 4 }}>
        <Box sx={{ backgroundColor: cardBg, borderRadius: "14px", border: `1.5px solid ${cardBorder}`, p: 2.5 }}>
          <Typography sx={{ fontSize: "1rem", fontWeight: 800, color: primaryTextColor, mb: 0.3 }}>
            Recent Visits
          </Typography>
          <Typography sx={{ fontSize: "0.75rem", color: secondaryTextColor, fontWeight: 500, mb: 2 }}>
            Latest patient encounters
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
            {visits.map((v, i) => {
              const isSelected = selectedVisit === i;
              return (
                <Box
                  key={i}
                  onClick={() => setSelectedVisit(i)}
                  sx={{
                    p: 1.4,
                    borderRadius: "10px",
                    cursor: "pointer",
                    backgroundColor: isSelected
                      ? isDark ? "rgba(56, 189, 248, 0.12)" : "#EFF6FF"
                      : isDark ? "transparent" : "#FFFFFF",
                    border: isSelected ? `1.5px solid ${tealMain}` : `1px solid ${cardBorder}`,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.4,
                    transition: "all 0.15s ease",
                    "&:hover": { transform: "translateY(-1px)" },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      fontSize: "0.8rem",
                      fontWeight: 700,
                      backgroundColor: isSelected ? tealMain : iconCircleBg,
                      color: isSelected ? "#FFFFFF" : tealMain,
                    }}
                  >
                    {v.name[0]}
                  </Avatar>
                  <Box>
                    <Typography sx={{ fontSize: "0.84rem", fontWeight: 700, color: primaryTextColor }}>
                      {v.name}
                    </Typography>
                    <Typography sx={{ fontSize: "0.72rem", color: secondaryTextColor }}>
                      {v.date} • <span style={{ color: tealMain, fontWeight: 600 }}>{v.type}</span>
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        <Box sx={{ backgroundColor: cardBg, borderRadius: "14px", border: `1.5px solid ${cardBorder}`, p: 3, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", pb: 2, borderBottom: `1px solid ${cardBorder}` }}>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 0.3 }}>
                <Typography sx={{ fontSize: "1.05rem", fontWeight: 800, color: primaryTextColor }}>
                  Claim Details & Settlement
                </Typography>
                <Chip
                  label={activeClaim.claimId}
                  size="small"
                  sx={{
                    height: "20px",
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    backgroundColor: iconCircleBg,
                    color: tealMain,
                    borderRadius: "5px",
                  }}
                />
              </Box>
              <Typography sx={{ fontSize: "0.78rem", color: secondaryTextColor }}>
                Patient: <strong style={{ color: primaryTextColor }}>{activeClaim.name}</strong>
              </Typography>
            </Box>

            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  borderColor: cardBorder,
                  color: primaryTextColor,
                  "&:hover": { borderColor: tealMain },
                }}
              >
                Export PDF
              </Button>
              <Button
                variant="contained"
                size="small"
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  backgroundColor: tealMain,
                  color: "#FFFFFF",
                  boxShadow: "none",
                  "&:hover": { backgroundColor: "#245E65" },
                }}
              >
                Process Claim
              </Button>
            </Box>
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" }, gap: 1.8, my: 2.5 }}>
            <Box sx={{ p: 1.8, borderRadius: "10px", backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "#F8FAFC", border: `1px solid ${cardBorder}` }}>
              <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: secondaryTextColor, textTransform: "uppercase" }}>Total Billed</Typography>
              <Typography sx={{ fontSize: "1.25rem", fontWeight: 800, color: primaryTextColor, mt: 0.3 }}>{activeClaim.amount}</Typography>
            </Box>
            <Box sx={{ p: 1.8, borderRadius: "10px", backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "#F8FAFC", border: `1px solid ${cardBorder}` }}>
              <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: secondaryTextColor, textTransform: "uppercase" }}>Insurance</Typography>
              <Typography sx={{ fontSize: "1.25rem", fontWeight: 800, color: "#10B981", mt: 0.3 }}>80% Covered</Typography>
            </Box>
            <Box sx={{ p: 1.8, borderRadius: "10px", backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "#F8FAFC", border: `1px solid ${cardBorder}` }}>
              <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: secondaryTextColor, textTransform: "uppercase" }}>Status</Typography>
              <Typography sx={{ fontSize: "1.05rem", fontWeight: 800, color: activeClaim.status === "Paid" ? "#10B981" : "#FB923C", mt: 0.4 }}>
                {activeClaim.status}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              p: 1.6,
              borderRadius: "10px",
              backgroundColor: iconCircleBg,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
              <ReceiptLongOutlinedIcon sx={{ color: tealMain, fontSize: "1.25rem" }} />
              <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: primaryTextColor }}>
                {activeClaim.type} • Logged on {activeClaim.date}
              </Typography>
            </Box>
            <Button
              size="small"
              endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: "0.9rem !important" }} />}
              sx={{ fontSize: "0.75rem", fontWeight: 700, color: tealMain, textTransform: "none" }}
            >
              Details
            </Button>
          </Box>
        </Box>
      </Box>

      {/* 5. Recent Encounters Table */}
      <Box sx={{ p: 2.8, borderRadius: "14px", backgroundColor: cardBg, border: `1.5px solid ${cardBorder}` }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Typography sx={{ fontSize: "1.05rem", fontWeight: 800, color: primaryTextColor }}>
            Recent Patient Encounters
          </Typography>
          <Button
            endIcon={<ArrowForwardOutlinedIcon sx={{ fontSize: "0.95rem !important" }} />}
            sx={{ fontSize: "0.78rem", fontWeight: 700, color: tealMain, textTransform: "none" }}
          >
            View All Records
          </Button>
        </Box>

        <TableContainer component={Paper} elevation={0} sx={{ backgroundColor: "transparent", overflowX: "hidden" }}>
          <Table sx={{ width: "100%" }}>
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    color: secondaryTextColor,
                    fontWeight: 700,
                    fontSize: "0.74rem",
                    textTransform: "uppercase",
                    backgroundColor: isDark ? "#162238" : "#F8FAFC",
                    borderBottom: `1px solid ${cardBorder}`,
                    py: 1.5,
                  },
                }}
              >
                <TableCell>Encounter ID</TableCell>
                <TableCell>Patient Name</TableCell>
                <TableCell>Chief Complaint</TableCell>
                <TableCell>Time Slot</TableCell>
                <TableCell>Physician</TableCell>
                <TableCell align="right">Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recentEncounters.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{
                    "&:hover": { backgroundColor: isDark ? "rgba(56, 189, 248, 0.04)" : "#F8FAFC" },
                    "& td": { borderBottom: `1px solid ${cardBorder}`, py: 1.4 },
                  }}
                >
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.82rem", color: tealMain }}>
                    {row.id}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Avatar
                        sx={{
                          width: 26,
                          height: 26,
                          fontSize: "0.72rem",
                          fontWeight: 700,
                          backgroundColor: iconCircleBg,
                          color: tealMain,
                        }}
                      >
                        {row.patient[0]}
                      </Avatar>
                      <Typography sx={{ fontSize: "0.84rem", fontWeight: 700, color: primaryTextColor }}>
                        {row.patient} <span style={{ fontSize: "0.75rem", fontWeight: 500, color: secondaryTextColor }}>({row.age}y)</span>
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.82rem", fontWeight: 600, color: primaryTextColor }}>
                    {row.condition}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.82rem", color: secondaryTextColor }}>
                    {row.time}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.82rem", fontWeight: 600, color: primaryTextColor }}>
                    {row.doctor}
                  </TableCell>
                  <TableCell align="right">
                    <Chip
                      label={row.status}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.68rem",
                        height: "20px",
                        backgroundColor:
                          row.status === "Completed"
                            ? isDark ? "rgba(52, 211, 153, 0.15)" : "#ECFDF5"
                            : row.status === "In Progress"
                            ? iconCircleBg
                            : isDark ? "rgba(251, 146, 60, 0.15)" : "#FEF3C7",
                        color:
                          row.status === "Completed"
                            ? "#10B981"
                            : row.status === "In Progress"
                            ? tealMain
                            : "#FB923C",
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default DashboardView;